import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import type { ThemeConfig } from 'antd';

export type ThemeMode = 'light' | 'dark' | 'system';
export const THEME_STORAGE_KEY = 'admin-theme-mode';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const parseMode = (value: string | null): ThemeMode =>
  value === 'light' || value === 'dark' ? value : 'system';

const readMode = (): ThemeMode => {
  try {
    return parseMode(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
};

const ThemeContext = createContext<{
  mode: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
} | undefined>(undefined);

// Share Ant Design's semantic colors with custom surfaces and chart SVGs.
const ThemeTokens: React.FC = () => {
  const { token } = theme.useToken();
  return <style>{`:root {
    --admin-bg: ${token.colorBgLayout};
    --admin-surface: ${token.colorBgContainer};
    --admin-fill: ${token.colorFillAlter};
    --admin-text: ${token.colorText};
    --admin-secondary: ${token.colorTextSecondary};
    --admin-border: ${token.colorBorder};
    --admin-primary: ${token.colorPrimaryText};
    --admin-success: ${token.colorSuccessText};
    --admin-warning: ${token.colorWarningText};
    --admin-error: ${token.colorErrorText};
    --admin-purple: ${token.purple6};
  }`}</style>;
};

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mode, updateMode] = useState<ThemeMode>(readMode);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia(DARK_QUERY).matches);
  const resolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;
  const themeConfig = useMemo<ThemeConfig>(() => ({
    algorithm: resolvedTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
  }), [resolvedTheme]);

  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => setSystemDark(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        updateMode(readMode());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      media.removeEventListener('change', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
    document.documentElement.style.backgroundColor = 'var(--admin-bg)';
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content', resolvedTheme === 'dark' ? '#141414' : '#ffffff'
    );
    // Existing message/Modal static APIs live outside the application tree.
    ConfigProvider.config({
      holderRender: (node) => <ConfigProvider locale={zhCN} theme={themeConfig}>{node}</ConfigProvider>,
    });
  }, [resolvedTheme, themeConfig]);

  const setMode = (next: ThemeMode) => {
    updateMode(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Switching still works when browser storage is unavailable.
    }
  };

  return (
    <ThemeContext.Provider value={{ mode, resolvedTheme, setMode }}>
      <ConfigProvider locale={zhCN} theme={themeConfig}>
        <ThemeTokens />
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
